<script setup>
import { onMounted, watch } from "vue";
import { usePage } from "@inertiajs/vue3";
import { useToastStore } from "@/stores/toast.js";

/**
 * Toast host (GH-935): bottom-left card stack in the brand style. Rendered
 * once per layout, next to the modalog host. Also promotes server flash
 * props into toasts so redirect flows (delete list, etc.) surface feedback
 * after the new page loads.
 */
const toastStore = useToastStore();
const page = usePage();

// Toasts parked before a full page load (window.location flows).
onMounted(() => toastStore.drainFlashed());

watch(
    () => page.props.flash,
    (flash) => {
        if (flash?.success) {
            toastStore.success(flash.success);
        }

        if (flash?.error) {
            toastStore.error(flash.error);
        }
    },
    { immediate: true },
);
</script>

<template>
    <!-- aria-live region: keep the container mounted so additions announce -->
    <div
        class="brand pointer-events-none fixed bottom-5 left-5 right-5 z-[60] flex flex-col-reverse items-stretch gap-3 sm:right-auto sm:w-[360px]"
        role="status"
        aria-live="polite"
    >
        <TransitionGroup
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="translate-y-3 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-for="toast in toastStore.toasts"
                :key="toast.id"
                class="pointer-events-auto flex items-center gap-3.5 rounded-brand bg-white py-3.5 pl-3.5 pr-3 shadow-brand-hero ring-1 ring-black/5"
                @mouseenter="toastStore.pause(toast.id, true)"
                @mouseleave="toastStore.pause(toast.id, false)"
            >
                <!-- Icon disc: gradient check for success, red alert for errors -->
                <span
                    class="grid size-8 shrink-0 place-items-center rounded-full text-white"
                    :style="
                        toast.type === 'success'
                            ? {
                                  backgroundImage:
                                      'linear-gradient(135deg, #ffb545 0%, #f21439 90%)',
                              }
                            : { backgroundColor: '#f21439' }
                    "
                    aria-hidden="true"
                >
                    <svg
                        v-if="toast.type === 'success'"
                        viewBox="0 0 14 11"
                        class="h-[11px] w-[14px]"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M1.5 5.5l4 4 7-8" />
                    </svg>
                    <span v-else class="text-[15px] font-bold leading-none"
                        >!</span
                    >
                </span>

                <p
                    class="min-w-0 flex-1 font-brand text-[14px] font-semibold leading-snug text-brand-navy"
                >
                    {{ toast.message }}
                </p>

                <button
                    type="button"
                    class="grid size-7 shrink-0 place-items-center self-start text-brand-navy/50 transition hover:text-brand-navy"
                    aria-label="Dismiss"
                    @click="toastStore.dismiss(toast.id)"
                >
                    <svg
                        viewBox="0 0 10 10"
                        class="size-2.5"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        aria-hidden="true"
                    >
                        <path d="M1 1l8 8M9 1L1 9" />
                    </svg>
                </button>
            </div>
        </TransitionGroup>
    </div>
</template>
